/**
 * Đăng ký service worker + phát tín hiệu "có bản mới" — Prompt.md §32, Bước 17.
 *
 * Không dùng `virtual:pwa-register/react`: nó tự dựng state bằng `useState` bên trong hook, nên
 * mỗi component gọi hook sẽ có một registration riêng. Ở đây store là module-level (giống
 * `MonitoringController`, quyết định #17) — SW đăng ký đúng một lần, React chỉ đọc snapshot.
 */

import { registerSW } from "virtual:pwa-register";

export interface UpdateState {
  /** Bản mới đã tải xong và đang đợi activate. */
  readonly updateReady: boolean;
  /** App đã cache đủ để chạy offline (chỉ app shell — model vision tải theo runtime cache). */
  readonly offlineReady: boolean;
}

const initial: UpdateState = { updateReady: false, offlineReady: false };

let state: UpdateState = initial;
const listeners = new Set<() => void>();
let applyUpdate: ((reload?: boolean) => Promise<void>) | null = null;

function setState(next: Partial<UpdateState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

/**
 * Gọi một lần lúc bootstrap. An toàn khi gọi lại (StrictMode mount hai lần trong dev) — lần thứ
 * hai không đăng ký thêm.
 */
export function initServiceWorker(): void {
  if (applyUpdate !== null) return;
  // Dev không build SW (`devOptions.enabled: false`) nên `registerSW` chỉ là no-op ở đó.
  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh: () => setState({ updateReady: true }),
    onOfflineReady: () => setState({ offlineReady: true }),
    onRegisterError: (error: unknown) => {
      // Không có UI cho lỗi này: app vẫn chạy đầy đủ khi thiếu SW, chỉ mất offline. Log để
      // Debug Mode (§25) trên thiết bị thật còn thấy được.
      console.warn("[pwa] không đăng ký được service worker", error);
    },
  });
}

/** Activate bản mới rồi reload. Chỉ gọi khi người dùng chủ động đồng ý (không tự động, §32). */
export function reloadWithUpdate(): void {
  setState({ updateReady: false });
  void applyUpdate?.(true);
}

/** Bỏ qua lần này — bản mới vẫn nằm chờ và sẽ tự activate ở lần mở app sau. */
export function dismissUpdate(): void {
  setState({ updateReady: false });
}

export function subscribeUpdates(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUpdateState(): UpdateState {
  return state;
}
